import { type MouseEvent } from "react";

import { t } from "i18next";

import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { IconButton, Tooltip } from "@mui/material";

import { notificationAtom } from "@/src/state/notificationAtom";

import { auctionOverlayIconBoxSx } from "@/src/components/molecules/auctionOverlayIcon.styles";

import { usePostHide } from "@/src/queries/dealer/crudHide";
import { DealerAuctionDTO } from "@/src/types/types";

import { useSetAtom } from "jotai";

interface HideAuctionIconButtonProps {
  auction: DealerAuctionDTO;
}

export const HideAuctionIconButton = ({
  auction
}: HideAuctionIconButtonProps) => {
  const { mutate: toggleHide } = usePostHide();
  const setNotification = useSetAtom(notificationAtom);

  const isAuctionHidden = auction?.isHidden || false;

  const handleToggleHidden = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!auction?.id) return;

    // Show notification immediately for instant feedback (optimistic)
    setNotification({
      text: isAuctionHidden
        ? `${t("dealer.userActions.hide.deleted", {
            regno: auction.auctionItem.vehicle?.regNo
          })}`
        : `${t("dealer.userActions.hide.added", {
            regno: auction.auctionItem.vehicle?.regNo
          })}`,
      variant: isAuctionHidden ? "info" : "success"
    });

    toggleHide(
      {
        auctionId: auction.id
      },
      {
        onError: (error) => {
          console.error("Failed to toggle hide status:", error);
          setNotification({
            text: `${t("dealer.userActions.hide.error")}`,
            variant: "error"
          });
        }
      }
    );
  };

  const label = isAuctionHidden
    ? t("components.auctionItem.showAuction")
    : t("components.auctionItem.hideAuction");

  return (
    <Tooltip title={label}>
      <IconButton
        aria-label={label}
        onClick={handleToggleHidden}
        sx={[
          auctionOverlayIconBoxSx,
          (theme) => ({
            color: isAuctionHidden
              ? theme.palette.primary.main
              : theme.palette.primary.contrastText,
            "&:hover": {
              color: theme.palette.primary.main,
              backgroundColor: "transparent"
            }
          })
        ]}
      >
        <VisibilityOffIcon sx={{ width: "24px", height: "24px" }} />
      </IconButton>
    </Tooltip>
  );
};
